import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Globe, Users, TrendingUp, BookOpen, LogOut, BarChart3, Eye, Download, Calendar, ChevronLeft, ChevronRight, X, GraduationCap, Award, Key } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { adminAPI } from '../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Drill-down state
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersData, setUsersData] = useState({ users: [], total: 0, page: 1, total_pages: 1 });
  const [usersFilter, setUsersFilter] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const data = await adminAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load statistics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminToken');
    toast({
      title: 'Logged out',
      description: 'Admin session ended',
    });
    navigate('/admin/login');
  };

  // Drill-down handlers
  const handleUsersDrilldown = async (filterType, filterLabel) => {
    setUsersFilter({ type: filterType, label: filterLabel });
    setUsersLoading(true);
    setShowUsersModal(true);
    
    try {
      const data = await adminAPI.getUsers(1, 50, filterType);
      setUsersData(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load user details',
        variant: 'destructive',
      });
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUsersPageChange = async (newPage) => {
    setUsersLoading(true);
    try {
      const data = await adminAPI.getUsers(newPage, 50, usersFilter?.type);
      setUsersData(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load page',
        variant: 'destructive',
      });
    } finally {
      setUsersLoading(false);
    }
  };

  // Export handlers - trigger actual file download
  const handleExport = (type) => {
    let url;
    switch (type) {
      case 'users':
        url = adminAPI.exportUsers(usersFilter?.type);
        break;
      case 'languages':
        url = adminAPI.exportLanguages();
        break;
      case 'lessons':
        url = adminAPI.exportLessons();
        break;
      case 'signups':
        url = adminAPI.exportSignups();
        break;
      default:
        return;
    }
    
    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_export.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: 'Export Started',
      description: `Downloading ${type} data as CSV`,
    });
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="border-b bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Globe className="h-8 w-8 text-amber-600" />
              <span className="text-xl font-bold text-slate-900">NomadLinguist Admin</span>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" onClick={() => navigate('/admin/change-password')} data-testid="admin-change-password-btn">
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
          <p className="text-xl text-slate-600">Monitor platform performance and user analytics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card 
            className="border-2 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => handleUsersDrilldown(null, 'All Users')}
            data-testid="total-users-card"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-slate-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.total_users?.toLocaleString() || 0}</div>
              <p className="text-xs text-slate-600 mt-1">Click to view all users</p>
            </CardContent>
          </Card>

          <Card 
            className="border-2 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => handleUsersDrilldown('daily', 'Daily Active Users')}
            data-testid="daily-active-card"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Daily Active</CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{stats?.active_users_daily?.toLocaleString() || 0}</div>
              <p className="text-xs text-slate-600 mt-1">Last 24 hours</p>
            </CardContent>
          </Card>

          <Card 
            className="border-2 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => handleUsersDrilldown('weekly', 'Weekly Active Users')}
            data-testid="weekly-active-card"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weekly Active</CardTitle>
              <BarChart3 className="h-4 w-4 text-sky-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-sky-600">{stats?.active_users_weekly?.toLocaleString() || 0}</div>
              <p className="text-xs text-slate-600 mt-1">Last 7 days</p>
            </CardContent>
          </Card>

          <Card 
            className="border-2 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => handleUsersDrilldown('monthly', 'Monthly Active Users')}
            data-testid="monthly-active-card"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Active</CardTitle>
              <Calendar className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{stats?.active_users_monthly?.toLocaleString() || 0}</div>
              <p className="text-xs text-slate-600 mt-1">Last 30 days</p>
            </CardContent>
          </Card>

          <Card className="border-2" data-testid="engagement-rate-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Engagement Rate</CardTitle>
              <Eye className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{stats?.engagement_rate || 0}%</div>
              <p className="text-xs text-slate-600 mt-1">Monthly / Total</p>
            </CardContent>
          </Card>
        </div>

        {/* Program Stats */}
        {stats?.program_stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="border-2 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="program-enrolled-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">15-Day Program Enrollments</CardTitle>
                <GraduationCap className="h-5 w-5 text-amber-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-700">{stats.program_stats.enrolled_users}</div>
                <p className="text-xs text-slate-600 mt-1">Users enrolled in structured learning</p>
              </CardContent>
            </Card>

            <Card className="border-2 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="program-completions-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Program Completions</CardTitle>
                <Award className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-700">{stats.program_stats.completions}</div>
                <p className="text-xs text-slate-600 mt-1">Users who completed all 15 days</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Analytics */}
        <Tabs defaultValue="signups" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="signups" data-testid="signups-tab">Signup Trends</TabsTrigger>
            <TabsTrigger value="languages" data-testid="languages-tab">Language Popularity</TabsTrigger>
            <TabsTrigger value="lessons" data-testid="lessons-tab">Lesson Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="signups" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-slate-900">Signup Trends</h2>
              <Button variant="outline" onClick={() => handleExport('signups')} data-testid="export-signups-btn">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="space-y-4">
                  {stats?.signups_by_period?.map((item, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`signup-period-${index}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <Calendar className="h-4 w-4 text-amber-700" />
                        </div>
                        <span className="font-semibold text-slate-900">{item.period}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-48 bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-amber-500 h-2 rounded-full" 
                            style={{ width: `${(item.count / (stats?.total_users || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="font-bold text-slate-900 w-16 text-right">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="languages" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-slate-900">Language Popularity</h2>
              <Button variant="outline" onClick={() => handleExport('languages')} data-testid="export-languages-btn">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
            {stats?.language_popularity?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stats.language_popularity.map((item, index) => (
                  <Card 
                    key={index} 
                    className="border-2 hover:shadow-lg transition-all"
                    data-testid={`language-card-${index}`}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{item.language}</span>
                        <span className="text-sm font-normal text-slate-600">{item.percentage}%</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="w-full bg-slate-200 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-amber-400 to-amber-600 h-3 rounded-full" 
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{item.users.toLocaleString()} users</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-2">
                <CardContent className="p-12 text-center">
                  <Globe className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No language learning data yet</p>
                  <p className="text-sm text-slate-400 mt-2">Data will appear when users start learning languages</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="lessons" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-slate-900">Lesson Activity</h2>
              <Button variant="outline" onClick={() => handleExport('lessons')} data-testid="export-lessons-btn">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
            <Card className="border-2">
              <CardContent className="p-6">
                {stats?.most_active_lessons?.length > 0 && stats.most_active_lessons[0].items_learned > 0 ? (
                  <div className="space-y-3">
                    {stats.most_active_lessons.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        data-testid={`lesson-item-${index}`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-sky-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{item.title}</p>
                            <p className="text-sm text-slate-600">{item.items_learned?.toLocaleString() || 0} items learned</p>
                          </div>
                        </div>
                        <Eye className="h-5 w-5 text-slate-400" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No lesson activity yet</p>
                    <p className="text-sm text-slate-400 mt-2">Data will appear when users complete lessons</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Users Drill-down Modal */}
      <Dialog open={showUsersModal} onOpenChange={setShowUsersModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{usersFilter?.label || 'All Users'}</span>
              <Button variant="outline" size="sm" onClick={() => handleExport('users')} data-testid="modal-export-users-btn">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </DialogTitle>
            <DialogDescription>
              Showing {usersData.users?.length || 0} of {usersData.total} users
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Table Header */}
                <div className="grid grid-cols-4 gap-4 p-3 bg-slate-100 rounded-lg font-semibold text-sm text-slate-600">
                  <div>Name</div>
                  <div>Email</div>
                  <div>Joined</div>
                  <div>Last Active</div>
                </div>
                
                {/* Table Rows */}
                {usersData.users?.map((user, index) => (
                  <div 
                    key={user.id} 
                    className="grid grid-cols-4 gap-4 p-3 bg-slate-50 rounded-lg text-sm hover:bg-slate-100 transition-colors"
                    data-testid={`user-row-${index}`}
                  >
                    <div className="font-medium text-slate-900 truncate">{user.name}</div>
                    <div className="text-slate-600 truncate">{user.email}</div>
                    <div className="text-slate-600">{new Date(user.created_at).toLocaleDateString()}</div>
                    <div className="text-slate-600">{new Date(user.last_active).toLocaleDateString()}</div>
                  </div>
                ))}
                
                {usersData.users?.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {usersData.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <Button 
                variant="outline" 
                size="sm"
                disabled={usersData.page <= 1 || usersLoading}
                onClick={() => handleUsersPageChange(usersData.page - 1)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {usersData.page} of {usersData.total_pages}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                disabled={usersData.page >= usersData.total_pages || usersLoading}
                onClick={() => handleUsersPageChange(usersData.page + 1)}
                data-testid="next-page-btn"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
